/**
 * AI COACH ENGINE (Rules-Based)
 *
 * Generates prioritized, actionable coaching suggestions by analysing:
 *   - Missing movement patterns
 *   - Muscle imbalances (anterior vs posterior)
 *   - Exercise redundancy
 *   - Fatigue risks
 *   - Progression gaps
 */

import { nanoid } from 'nanoid';
import type {
  Week,
  VolumeMetrics,
  FatigueMetrics,
  CoachSuggestion,
  MovementPattern,
  MuscleGroup,
  TrainingLevel,
  ProgressionModel,
} from '../types';
import { MOVEMENT_PATTERN_LABELS, ALL_MUSCLE_GROUPS, MUSCLE_GROUP_LABELS } from '../types';

// ── Pattern requirements ────────────────────

const CORE_PATTERNS: MovementPattern[] = [
  'squat', 'hinge', 'horizontal_push', 'vertical_push', 'horizontal_pull', 'vertical_pull',
];

// ── Posterior chain muscles ────────────────
const POSTERIOR_MUSCLES: MuscleGroup[] = ['hamstrings', 'glutes', 'upper_back', 'lats', 'rear_delts'];
const ANTERIOR_MUSCLES: MuscleGroup[] = ['quads', 'chest', 'front_delts', 'abs'];

// ── Main engine ─────────────────────────────

export function generateCoachSuggestions(
  week: Week,
  volume: VolumeMetrics,
  fatigue: FatigueMetrics,
  trainingLevel: TrainingLevel,
  progressionModel: ProgressionModel
): CoachSuggestion[] {
  const suggestions: CoachSuggestion[] = [];

  // ════════════════════════════════════════
  // 1. MISSING MOVEMENT PATTERNS
  // ════════════════════════════════════════
  const presentPatterns = new Set<MovementPattern>();
  const exerciseNames = new Map<string, number>(); // name → count

  for (const session of week.sessions) {
    for (const ex of session.exercises) {
      presentPatterns.add(ex.movementPattern);
      exerciseNames.set(ex.name, (exerciseNames.get(ex.name) || 0) + 1);
    }
  }

  for (const pattern of CORE_PATTERNS) {
    if (!presentPatterns.has(pattern)) {
      suggestions.push({
        id: nanoid(),
        type: 'missing_pattern',
        severity: 'warning',
        title: `Brakuje: ${MOVEMENT_PATTERN_LABELS[pattern]}`,
        description: `Twój program nie zawiera ćwiczeń ${MOVEMENT_PATTERN_LABELS[pattern].toLowerCase()}. Ten wzorzec ruchu jest kluczowy dla zrównoważonego rozwoju. Rozważ dodanie go do jednej z sesji.`,
        actionable: true,
      });
    }
  }

  if (!presentPatterns.has('core')) {
    suggestions.push({
      id: nanoid(),
      type: 'missing_pattern',
      severity: 'info',
      title: 'Brak bezpośredniego treningu core',
      description: 'Choć ćwiczenia złożone trenują core pośrednio, dodanie 2-3 serii bezpośredniego treningu core (deska, rollout, Pallof press) poprawia stabilność i zmniejsza ryzyko kontuzji.',
      actionable: true,
    });
  }

  // ════════════════════════════════════════
  // 2. POSTERIOR CHAIN ANALYSIS
  // ════════════════════════════════════════
  const posteriorVolume = volume.muscleVolumes
    .filter((mv) => POSTERIOR_MUSCLES.includes(mv.muscle))
    .reduce((sum, mv) => sum + mv.totalSets, 0);

  const anteriorVolume = volume.muscleVolumes
    .filter((mv) => ANTERIOR_MUSCLES.includes(mv.muscle))
    .reduce((sum, mv) => sum + mv.totalSets, 0);

  if (anteriorVolume > 0 && posteriorVolume / Math.max(anteriorVolume, 1) < 0.7) {
    suggestions.push({
      id: nanoid(),
      type: 'muscle_imbalance',
      severity: 'warning',
      title: 'Niewystarczający łańcuch tylny',
      description: `Objętość łańcucha tylnego (${posteriorVolume.toFixed(0)} serii) jest znacznie niższa niż przedniego (${anteriorVolume.toFixed(0)} serii). Ten brak równowagi może powodować problemy posturalne i zwiększać ryzyko kontuzji. Dodaj więcej wiosłowań, martwych ciągów i pracy na tył barków.`,
      actionable: true,
    });
  }

  // ── Side delt / rear delt check ──
  const sideDelts = volume.muscleVolumes.find((m) => m.muscle === 'side_delts');
  const rearDelts = volume.muscleVolumes.find((m) => m.muscle === 'rear_delts');
  if (sideDelts && sideDelts.totalSets < sideDelts.mev) {
    suggestions.push({
      id: nanoid(),
      type: 'muscle_imbalance',
      severity: 'info',
      title: 'Boki barków poniżej MEV',
      description: 'Boczne części mięśnia naramiennego są poniżej minimalnej efektywnej objętości. Dodaj unoszenia boczne dla szerokości barków i zdrowia stawów.',
      actionable: true,
    });
  }
  if (rearDelts && rearDelts.totalSets < rearDelts.mev) {
    suggestions.push({
      id: nanoid(),
      type: 'muscle_imbalance',
      severity: 'info',
      title: 'Tył barków poniżej MEV',
      description: 'Tylne części mięśnia naramiennego są niedotrenowane. Wyciąg do twarzy lub rozpietki na tył barków pomogą zrównoważyć zdrowie barków.',
      actionable: true,
    });
  }

  // ════════════════════════════════════════
  // 3. EXERCISE REDUNDANCY
  // ════════════════════════════════════════
  for (const [name, count] of exerciseNames) {
    if (count >= 3) {
      suggestions.push({
        id: nanoid(),
        type: 'exercise_redundancy',
        severity: 'warning',
        title: `"${name}" występuje ${count}×`,
        description: `To samo ćwiczenie jest używane ${count} razy w tym tygodniu. Rozważ użycie wariantów, aby targetować mięśnie z różnych kątów i zmniejszyć powtarzające się obciążenie.`,
        actionable: true,
      });
    }
  }

  // Check pattern over-reliance (>50% of total sets from one pattern)
  const patternSets: Record<string, number> = {};
  for (const session of week.sessions) {
    for (const ex of session.exercises) {
      patternSets[ex.movementPattern] = (patternSets[ex.movementPattern] || 0) + ex.sets;
    }
  }
  const totalSets = volume.totalSets || 1;
  for (const [pattern, sets] of Object.entries(patternSets)) {
    if (sets / totalSets > 0.35 && totalSets > 10) {
      suggestions.push({
        id: nanoid(),
        type: 'exercise_redundancy',
        severity: 'info',
        title: `Duże poleganie na ${MOVEMENT_PATTERN_LABELS[pattern as MovementPattern] || pattern}`,
        description: `${Math.round((sets / totalSets) * 100)}% całkowitej objętości pochodzi z jednego wzorca ruchu. Zdywersyfikuj, aby zmniejszyć ryzyko przeciążenia.`,
        actionable: true,
      });
    }
  }

  // ════════════════════════════════════════
  // 4. FATIGUE RISK FLAGS
  // ════════════════════════════════════════
  if (fatigue.overallFatigue > 75) {
    suggestions.push({
      id: nanoid(),
      type: 'fatigue_risk',
      severity: 'critical',
      title: 'Wysokie ogólne zmęczenie',
      description: `Indeks zmęczenia wynosi ${fatigue.overallFatigue}/100. Ten poziom grozi przetrenowaniem. Zmniejsz całkowitą objętość, obniż szczytowe intensywności lub zaplanuj tydzień odciążający.`,
      actionable: true,
    });
  } else if (fatigue.overallFatigue > 55) {
    suggestions.push({
      id: nanoid(),
      type: 'fatigue_risk',
      severity: 'info',
      title: 'Umiarkowane zmęczenie - monitoruj',
      description: `Indeks zmęczenia wynosi ${fatigue.overallFatigue}/100. Możliwe do opanowania, ale pilnuj jakości regeneracji.`,
      actionable: false,
    });
  }

  // MRV-exceeding muscles
  const overMRV = volume.muscleVolumes.filter((mv) => mv.status === 'exceeds_mrv');
  for (const mv of overMRV) {
    suggestions.push({
      id: nanoid(),
      type: 'fatigue_risk',
      severity: 'critical',
      title: `${MUSCLE_GROUP_LABELS[mv.muscle]} powyżej MRV`,
      description: `${MUSCLE_GROUP_LABELS[mv.muscle]} ma ${mv.totalSets} serii (MRV: ${mv.mrv}). Zmniejsz objętość, aby zapobiec niefunkcjonalnemu przeciążeniu.`,
      actionable: true,
    });
  }

  // ════════════════════════════════════════
  // 5. PROGRESSION & GENERAL
  // ════════════════════════════════════════
  if (trainingLevel === 'beginner' && progressionModel !== 'linear') {
    suggestions.push({
      id: nanoid(),
      type: 'progression',
      severity: 'info',
      title: 'Rozważ progresję liniową',
      description: 'Początkujący najszybciej postępują z prostą progresją liniową (dodawanie ciężaru co sesję). Bardziej złożone modele periodyzacji mogą poczekać.',
      actionable: true,
    });
  }

  if (['advanced', 'elite'].includes(trainingLevel) && progressionModel === 'linear') {
    suggestions.push({
      id: nanoid(),
      type: 'progression',
      severity: 'warning',
      title: 'Progresja liniowa może się zatrzymać',
      description: 'Zaawansowani rzadko utrzymują liniowe przyrosty. Rozważ falowanie lub periodyzację blokową dla dalszych postępów.',
      actionable: true,
    });
  }

  // Check if any session has 0 exercises
  for (const session of week.sessions) {
    if (session.exercises.length === 0) {
      suggestions.push({
        id: nanoid(),
        type: 'general',
        severity: 'info',
        title: `Pusta sesja: ${session.day}`,
        description: `"${session.focus}" w ${session.day} nie ma ćwiczeń. Dodaj ćwiczenia lub usuń sesję.`,
        actionable: true,
      });
    }
  }

  // Intensity distribution hints
  const { low, moderate, high, max } = volume.intensityDistribution;
  const intTotal = low + moderate + high + max;
  if (intTotal > 0 && max / intTotal > 0.35) {
    suggestions.push({
      id: nanoid(),
      type: 'fatigue_risk',
      severity: 'warning',
      title: 'Zbyt dużo maksymalnej intensywności',
      description: `${Math.round((max / intTotal) * 100)}% serii jest przy RPE 9+. Utrzymujący się wysoki wysiłek pogarsza regenerację. Trzymaj serie maksymalnego wysiłku na około 20-30% całkowitej objętości.`,
      actionable: true,
    });
  }

  // ── NEW RULES (Round 4) ────────────────────

  // 1. Core neglect
  if ((volume.muscleVolumes.find((m) => m.muscle === 'abs')?.totalSets || 0) === 0) {
    suggestions.push({
      id: nanoid(),
      type: 'missing_pattern',
      severity: 'info',
      title: 'Brak ćwiczeń na mięśnie brzucha (Core)',
      description: 'Zaleca się dodanie co najmniej 1-2 ćwiczeń na mięśnie brzucha (np. plank, ab-wheel) w skali tygodnia, aby wzmocnić stabilizację centralną.',
      actionable: true,
    });
  }

  // 2. Calf neglect
  if ((volume.muscleVolumes.find((m) => m.muscle === 'calves')?.totalSets || 0) === 0) {
    suggestions.push({
      id: nanoid(),
      type: 'missing_pattern',
      severity: 'info',
      title: 'Brak ćwiczeń na łydki',
      description: 'W planie brakuje ćwiczeń izolowanych na łydki (np. wspięcia na palce). Jeśli zależy Ci na ich rozwoju, dodaj 2-4 serie w tygodniu.',
      actionable: true,
    });
  }

  // 3. Inadequate rest on heavy compounds
  let inadequateRest = false;
  week.sessions.forEach(session => {
    session.exercises.forEach(ex => {
      if (['squat', 'hinge', 'horizontal_push', 'vertical_push', 'horizontal_pull', 'vertical_pull'].includes(ex.movementPattern)) {
        const rpe = ex.loadType === 'rpe' ? ex.load : ex.load / 10;
        if (rpe >= 8 && ex.rest < 120) {
          inadequateRest = true;
        }
      }
    });
  });
  if (inadequateRest) {
    suggestions.push({
      id: nanoid(),
      type: 'fatigue_risk',
      severity: 'warning',
      title: 'Zbyt krótki odpoczynek w ciężkich ćwiczeniach wielostawowych',
      description: 'Zauważono ćwiczenia wielostawowe (np. przysiady, wyciskania) z wysokim naprężeniem (RPE ≥8) i krótkim czasem odpoczynku (<2 minuty). Rozważ wydłużenie przerw do 2-3 minut dla optymalnej regeneracji i siły.',
      actionable: true,
    });
  }

  // 4. Volume vs Frequency mismatch
  const daysWithWorkouts = week.sessions.length;
  const totalWeeklySets = volume.totalSets;
  if (daysWithWorkouts > 0 && totalWeeklySets / daysWithWorkouts > 30) {
    suggestions.push({
      id: nanoid(),
      type: 'exercise_redundancy',
      severity: 'warning',
      title: 'Zbyt duża objętość na jedną sesję',
      description: `Planujesz średnio ponad 30 serii na trening. To bardzo dużo i może prowadzić do nagromadzenia tzw. "junk volume". Rozważ rozłożenie tej objętości na więcej dni treningowych (zwiększenie częstotliwości).`,
      actionable: true,
    });
  }

  // 5. Overlapping exercises
  let overlappingRedundancy = false;
  week.sessions.forEach(session => {
    for (let i = 0; i < session.exercises.length - 1; i++) {
      if (session.exercises[i].primaryMuscle === session.exercises[i + 1].primaryMuscle) {
        overlappingRedundancy = true;
      }
    }
  });
  if (overlappingRedundancy) {
    suggestions.push({
      id: nanoid(),
      type: 'exercise_redundancy',
      severity: 'info',
      title: 'Nakładające się na siebie ćwiczenia na tę samą partię',
      description: 'Zauważono wykonywanie dwóch ćwiczeń na tę samą główną partię mięśniową (np. klatkę) bezpośrednio po sobie w jednej sesji. Rozważ przeplatanie ćwiczeń na antagonistyczne partie (tzw. superserie lub po prostu naprzemiennie).',
      actionable: true,
    });
  }

  // Sort by severity
  const severityOrder = { critical: 0, warning: 1, info: 2 };
  suggestions.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return suggestions;
}
